import React, { useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowUpRightIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { footerInfo, laebelArms, navLinks } from '../data/landing';
import { SectionLink } from './SectionLink';
type NavMenuProps = {
  id: string;
  onClose: () => void;
};
const ease = [0.23, 1, 0.32, 1] as const;
const labelClass = 'text-[12px] font-medium uppercase tracking-[0.04em]';

/**
 * Site menu dropping from the dock: the key page parts as numbered serif rows,
 * the three arms of LAEBEL, Dubai HQ contact and two calls to action.
 */
export function NavMenu({
  id,
  onClose
}: NavMenuProps) {
  const reduce = useReducedMotion();
  const links = [...navLinks, {
    label: 'FAQ',
    href: '#faq'
  }];
  const time = useDubaiTime();
  return <motion.div id={id} role="dialog" aria-label="Site menu" initial={{
    opacity: 0,
    y: -8,
    scale: 0.98
  }} animate={{
    opacity: 1,
    y: 0,
    scale: 1
  }} exit={{
    opacity: 0,
    y: -8,
    scale: 0.98
  }} transition={{
    duration: 0.22,
    ease
  }} className="pointer-events-auto mt-2 max-h-[calc(100vh-110px)] w-[min(600px,calc(100vw-24px))] origin-top overflow-y-auto rounded-card border border-white/10 bg-[#1c1c1c]/85 text-white shadow-[0_24px_60px_rgba(0,0,0,0.28)] backdrop-blur-xl backdrop-saturate-150" data-id="element-258" data-mp="udkr2xo6jxbj">
      <div className="px-6 pb-6 pt-5 md:px-8" data-id="element-259" data-mp="1vlv45cnwplx6">
        {/* Label row */}
        <div className="flex items-baseline justify-between pb-3" data-id="element-260" data-mp="xxkz0ostqi1u">
          <p className={`${labelClass} text-white/60`} data-id="element-261" data-mp="12ubdj7ozd5l5">LAEBEL — Single source of origin</p>
          <span className={`${labelClass} text-white/40`} aria-hidden="true" data-id="element-262" data-mp="17to9chxtxmzv">
            ({String(links.length).padStart(2, '0')})
          </span>
        </div>

        {/* Key page parts */}
        <ul className="border-b border-white/10" data-id="element-263" data-mp="2j9w4hyaxlw1i">
          {links.map((link, i) => <motion.li key={link.label} initial={reduce ? false : {
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.2,
          ease,
          delay: reduce ? 0 : 0.04 + i * 0.035
        }} className="border-t border-white/10" data-id="element-264" data-mp="2naoeitrmo4yu" data-mpi={i}>
              <SectionLink hash={link.href} onClick={onClose} className="group grid grid-cols-[40px_1fr_auto] items-center py-2.5 focus:outline-none focus-visible:bg-white/5" data-id="element-265" data-mp="x7edyqxa823y">
                <span className="text-[12px] tabular-nums text-white/40 transition-colors duration-150 group-hover:text-white/70" data-id="element-266" data-mp="4iqris0sr6ot">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="font-serif text-[26px] font-normal leading-[1.1] tracking-[-0.01em] text-white/70 transition-colors duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] group-hover:text-white md:text-[30px]" data-id="element-267" data-mp="e9blg25izn1e">
                  {link.label}
                </span>
                <ArrowUpRightIcon className="h-4 w-4 text-white/0 transition-[color,transform] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-white" aria-hidden="true" data-id="element-268" data-mp="3cx91x0e7mybl" />
              </SectionLink>
            </motion.li>)}
        </ul>

        {/* The three arms */}
        <div className="mt-6" data-id="element-269" data-mp="1of690rrl4w2y">
          <p className={`${labelClass} text-white/40`} data-id="element-270" data-mp="1ocab33fqm7ei">The ecosystem</p>
          <ul className="mt-3 grid gap-2 sm:grid-cols-3" data-id="element-271" data-mp="2c4h01hj4vc3w">
            {laebelArms.map(arm => <li key={arm.name} data-id="element-272" data-mp="2g50w0hrz09og">
                <SectionLink hash={arm.href} onClick={onClose} className="block h-full rounded-control bg-white/[0.04] p-3 transition-colors duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] hover:bg-white/[0.08] focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-273" data-mp="2g37zvwezh7up">
                  <span className="block text-[14px] text-white" data-id="element-274" data-mp="vqqxif7nwvwd">LAEBEL {arm.name}</span>
                  <span className="mt-1 block text-[13px] leading-[1.4] text-white/50" data-id="element-275" data-mp="1azm9hn9jn37e">{arm.line}</span>
                </SectionLink>
              </li>)}
          </ul>
        </div>

        {/* Contact */}
        <div className="mt-6 flex items-end justify-between gap-6" data-id="element-276" data-mp="127u0zi42y45g">
          <div data-id="element-277" data-mp="x8hc6rtnen08">
            <p className={`${labelClass} text-white/40`} data-id="element-278" data-mp="x90jlelo9t00">Get in touch</p>
            <a href={`mailto:${footerInfo.email}`} className="mt-2 block text-[15px] text-white transition-opacity duration-150 hover:opacity-70" data-id="element-279" data-mp="1txyjtcc6yj4k">
              {footerInfo.email}
            </a>
          </div>
          <div className="text-right" data-id="element-280" data-mp="1kvp7cgxd4foj">
            <p className={`${labelClass} text-white/40`} data-id="element-281" data-mp="1fypc7121wxc7">Dubai HQ</p>
            <p className="mt-2 text-[15px] tabular-nums text-white/70" data-id="element-282" data-mp="1g0jnrorfnd3m">
              {time} <span className="text-white/40" data-id="element-283" data-mp="9jy6mk1zgobu">GMT+4</span>
            </p>
          </div>
        </div>

        {/* Calls to action, same as the page's buttons */}
        <div className="mt-6 grid grid-cols-1 gap-2 sm:grid-cols-2" data-id="element-284" data-mp="3ql9d0ez5tzpk">
          <Link to="/contact" onClick={onClose} className="group flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-control bg-white text-[13px] uppercase tracking-[0.04em] text-black transition-colors duration-150 hover:bg-white/85 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black" data-id="element-285" data-mp="3qiwo1huzip30">
            Start a project
            <ArrowUpRightIcon className="h-4 w-4 transition-transform duration-150 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" aria-hidden="true" data-id="element-286" data-mp="i3mpgjqb01zz" />
          </Link>
          <Link to="/contact?topic=equity" onClick={onClose} className="group flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-control border border-white/30 text-[13px] uppercase tracking-[0.04em] text-white transition-colors duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] hover:border-white hover:bg-white hover:text-black focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-287" data-mp="s2c8wgxuluic">
            Discuss equity
            <ArrowUpRightIcon className="h-4 w-4 transition-transform duration-150 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" aria-hidden="true" data-id="element-288" data-mp="2vtjp1igc5kst" />
          </Link>
        </div>
      </div>
    </motion.div>;
}

/** Current Dubai time as HH:MM, updated every 30 seconds. */
function useDubaiTime() {
  const format = () => new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Dubai',
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date());
  const [time, setTime] = useState(format);
  useEffect(() => {
    const t = window.setInterval(() => setTime(format()), 30000);
    return () => window.clearInterval(t);
  }, []);
  return time;
}